/// <reference types="google.maps" />
import { inject, Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { from, Observable, of, switchMap } from 'rxjs';
import { ENV_CONFIG, EnvConfig } from '../env';
import { CoordinatesModel } from '../interfaces';

export interface CitySuggestion {
  readonly name: string;
  readonly placeId: string;
}

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private readonly env: EnvConfig = inject(ENV_CONFIG);

  private readonly loader: Loader = new Loader({
    apiKey: this.env.mapApiKey,
    libraries: ['places']
  });

  private loadPromise: Promise<typeof google> | null = null;

  searchCities(query: string): Observable<CitySuggestion[]> {
    const input: string = query?.trim() ?? '';
    if (!input) return of([]);

    return from(this.load()).pipe(switchMap(() => from(this.getPredictions(input))));
  }

  resolveCoordinates(placeId: string): Observable<CoordinatesModel | null> {
    return from(this.load()).pipe(switchMap(() => from(this.geocodeByPlaceId(placeId))));
  }

  reverseGeocode(coordinates: CoordinatesModel): Observable<string | null> {
    return from(this.load()).pipe(switchMap(() => from(this.geocodeByLocation(coordinates))));
  }

  private load(): Promise<typeof google> {
    if (!this.loadPromise) this.loadPromise = this.loader.load();
    return this.loadPromise;
  }

  private getPredictions(input: string): Promise<CitySuggestion[]> {
    const service: google.maps.places.AutocompleteService = new google.maps.places.AutocompleteService();

    return new Promise<CitySuggestion[]>((resolve: (value: CitySuggestion[]) => void) => {
      service.getPlacePredictions(
        { input, componentRestrictions: { country: 'pl' } },
        (
          predictions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            resolve([]);
            return;
          }

          resolve(
            predictions.map((prediction: google.maps.places.AutocompletePrediction) => ({
              name: prediction.description,
              placeId: prediction.place_id
            }))
          );
        }
      );
    });
  }

  private geocodeByPlaceId(placeId: string): Promise<CoordinatesModel | null> {
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    return new Promise<CoordinatesModel | null>((resolve: (value: CoordinatesModel | null) => void) => {
      geocoder.geocode(
        { placeId },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          const location: google.maps.LatLng | undefined =
            status === google.maps.GeocoderStatus.OK ? results?.[0]?.geometry?.location : undefined;

          resolve(location ? { latitude: location.lat(), longitude: location.lng() } : null);
        }
      );
    });
  }

  private geocodeByLocation(coordinates: CoordinatesModel): Promise<string | null> {
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    return new Promise<string | null>((resolve: (value: string | null) => void) => {
      geocoder.geocode(
        { location: { lat: coordinates.latitude, lng: coordinates.longitude } },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status !== google.maps.GeocoderStatus.OK || !results) {
            resolve(null);
            return;
          }

          const localityResult: google.maps.GeocoderResult =
            results.find((result: google.maps.GeocoderResult) => result.types.includes('locality')) ?? results[0];

          const locality: google.maps.GeocoderAddressComponent | undefined = localityResult?.address_components?.find(
            (component: google.maps.GeocoderAddressComponent) => component.types.includes('locality')
          );

          resolve(locality?.long_name ?? null);
        }
      );
    });
  }
}
